/* eslint-disable max-len */
// src/components/contentHub/PostPreviewer.js

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiRefreshCw, FiLoader, FiZap, FiCheckCircle, FiFacebook, FiInstagram, FiLayout, FiArrowLeft, FiVideo } from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';
import TextareaAutosize from 'react-textarea-autosize';
import Modal from '../common/Modal';
import { isVideoTemplate } from '../shared/utils/mediaTypeUtils';
import { useToast } from '../shared/contexts/ToastContext';

// Helper: Platform Icons
const PlatformIcon = ({ platform, className }) => {
    if (platform === 'facebook') return <FiFacebook className={className} />;
    if (platform === 'instagram') return <FiInstagram className={className} />;
    if (platform === 'linkedin') return <FaLinkedin className={className} />;
    return <FiLayout className={className} />;
};

/**
 * Sub-component to handle editing of single or multi-platform content.
 */
const VariantEditor = ({ post, index, onEdit, disabled, adjustments }) => {
    const [activeTab, setActiveTab] = useState(null);

    // FIX: useMemo ensures 'platforms' array reference is stable
    const platforms = useMemo(() => {
        return typeof post.content === 'object' && post.content !== null
            ? Object.keys(post.content)
            : ['default'];
    }, [post.content]);

    // Set initial tab
    useEffect(() => {
        if (!activeTab && platforms.length > 0) {
            setActiveTab(platforms[0]);
        }
    }, [platforms, activeTab]);

    const currentContent = activeTab === 'default'
        ? post.content
        : post.content?.[activeTab] || '';

    const handleTextChange = (e) => {
        onEdit(index, e.target.value, activeTab === 'default' ? null : activeTab);
    };

    // Template detection for current tab
    const currentTemplate = activeTab && activeTab !== 'default' ? adjustments?.templates?.[activeTab] || '' : '';
    const isReel = currentTemplate.includes('reel');
    const isStory = currentTemplate.includes('story');
    const isVideo = isVideoTemplate(currentTemplate);
    const isLinkedIn = activeTab === 'linkedin';

    // Word count for reels
    const wordCount = currentContent ? currentContent.trim().split(/\s+/).filter(Boolean).length : 0;
    // Character count for LinkedIn (3000 char limit)
    const charCount = currentContent ? currentContent.length : 0;

    return (
        <div className="mt-4">
            {/* Tabs (Only if multiple variants exist) */}
            {platforms.length > 1 && (
                <div className="flex space-x-2 mb-3 border-b border-white/10 pb-1">
                    {platforms.map(p => (
                        <button
                            key={p}
                            onClick={() => setActiveTab(p)}
                            className={`flex items-center px-4 py-2 text-xs font-bold rounded-t-xl transition-all ${
                                activeTab === p
                                ? 'bg-slate-blue text-white border-b-2 border-slate-blue shadow-[0_-5px_15px_rgba(99,102,241,0.2)]'
                                : 'text-cream/40 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            <PlatformIcon platform={p} className="mr-2 h-3 w-3" />
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                    ))}
                </div>
            )}

            {/* Story overlay label */}
            {isStory && (
                <p className="text-xs text-amber-400/80 font-medium mb-2">This text will be used as Story overlay.</p>
            )}

            <TextareaAutosize
                value={currentContent || ''}
                onChange={handleTextChange}
                disabled={disabled}
                minRows={isStory ? 2 : isReel ? 3 : 4}
                className="block w-full bg-white/5 border border-white/10 text-cream text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-slate-blue/50 focus:ring-1 focus:ring-slate-blue/50 focus:bg-white/10 transition-all placeholder-white/20 resize-none leading-relaxed"
                placeholder={isStory ? "Story overlay text (max 10 words)..." : isReel ? "Reel caption (short hook + CTA)..." : activeTab === 'default' ? "Generated content..." : `Content for ${activeTab}...`}
            />

            {/* Reel word counter */}
            {isReel && (
                <p className={`text-[10px] mt-1 font-medium ${wordCount > 60 ? 'text-red-400' : 'text-cream/30'}`}>
                    {wordCount}/60 words {wordCount > 60 && '(over limit)'}
                </p>
            )}

            {/* LinkedIn character counter */}
            {isLinkedIn && (
                <p className={`text-[10px] mt-1 font-medium ${charCount > 3000 ? 'text-red-400' : 'text-cream/30'}`}>
                    {charCount.toLocaleString()}/3,000 characters {charCount > 3000 && '(over limit)'}
                </p>
            )}

            {/* Video badge */}
            {isVideo && (
                <div className="flex items-center mt-2 text-[10px] text-purple-400/80 font-bold uppercase tracking-wider">
                    <FiVideo className="mr-1 h-3 w-3" /> Video will be generated in next step
                </div>
            )}
        </div>
    );
};

/**
 * Sub-component to display carousel slide headlines
 */


function PostPreviewer({
    project,
    contentPlan,
    posts,
    onGeneratePosts,
    onImprovePostElement,
    onEditPostText,
    onConfirmPosts,
    loading,
    onBack,
    adjustments
}) {
    const [showImproveModal, setShowImproveModal] = useState(false);
    const [improveInstruction, setImproveInstruction] = useState('');
    const [improvePostIndex, setImprovePostIndex] = useState(null);
    const [isImprovingElement, setIsImprovingElement] = useState(false);
    
    const { addToast } = useToast();
    const [regenerationTarget, setRegenerationTarget] = useState(null);

    // Initial Generation Trigger
    useEffect(() => {
        const needsGeneration = posts.length > 0 && posts.some(p => p.content === null || p.content === undefined);
        if (needsGeneration && !loading) {
            console.info("PostPreviewer: Triggering initial generation.");
            const subjectsToGenerate = posts
                .filter(p => p.content === null || p.content === undefined)
                .map(p => p.subject);
            if (subjectsToGenerate.length > 0) {
                onGeneratePosts(subjectsToGenerate);
            }
        }
    }, [posts, loading, onGeneratePosts]);

    useEffect(() => {
        if (!loading) {
            setRegenerationTarget(null);
        }
    }, [loading]);

    // Helper: Check if content exists
    const hasContent = (post) => {
        if (!post.content) return false;
        if (typeof post.content === 'string') return post.content.trim().length > 0;
        return Object.values(post.content).some(v => v && v.trim().length > 0);
    };



    const hasGeneratedText = posts.some(hasContent);
    const canConfirm = hasGeneratedText && !loading && !isImprovingElement;

    // Handlers
    const handleRegenerateAll = useCallback(() => {
        if (loading || posts.length === 0) return;
        setRegenerationTarget('all');
        const subjects = posts.map(p => p.subject);
        onGeneratePosts(subjects);
    }, [loading, posts, onGeneratePosts]);

    const handleRegeneratePost = useCallback((index) => {
        const subject = posts[index]?.subject;
        if (loading || !subject) return;
        setRegenerationTarget(index);
        onGeneratePosts([subject]);
    }, [loading, posts, onGeneratePosts]);

    const handleImproveClick = useCallback((index) => {
        setImprovePostIndex(index);
        setImproveInstruction('');
        setShowImproveModal(true);
    }, []);

    const handleConfirmImprove = useCallback(async () => {
        const post = posts[improvePostIndex];
        if (!improveInstruction.trim() || improvePostIndex === null || !post) return;
        
        let originalContent = post.content;
        let targetPlatform = null;

        if (typeof post.content === 'object' && post.content !== null) {
            targetPlatform = Object.keys(post.content)[0];
            originalContent = post.content[targetPlatform];
        }

        if (!originalContent) return;

        setIsImprovingElement(true);
        setShowImproveModal(false);

        try {
           const payload = {
                projectId: project.id,
                contentPlan: contentPlan || {},
                originalPost: originalContent,
                elementToImprove: originalContent,
                improvementInstruction: improveInstruction.trim(),
                adjustments: contentPlan?.settings?.adjustments || {},
            };
            const result = await onImprovePostElement(payload);
            if (result.data.success) {
                onEditPostText(improvePostIndex, result.data.improvedElement, targetPlatform);
            } else {
                throw new Error(result.data.error || "Failed to improve element.");
            }
        } catch(err) {
            addToast("Failed to improve content. Please try again.", "error");
        } finally {
            setIsImprovingElement(false);
            setImprovePostIndex(null);
        }
    }, [improveInstruction, improvePostIndex, posts, onImprovePostElement, onEditPostText, project.id, contentPlan, addToast]);

    if (!posts || posts.length === 0) {
        return <div className="text-center p-6 text-gray-500"><p>Waiting for post plan...</p></div>;
    }

    return (
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 relative overflow-hidden">
            
            {/* Header */}
            <div className="p-8 border-b border-white/10 bg-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                    <button 
                        onClick={onBack}
                        className="mr-6 p-3 text-cream/40 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                        title="Back to Strategy"
                    >
                        <FiArrowLeft className="h-6 w-6" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">Preview & Edit</h2>
                        <p className="text-sm text-cream/60 mt-1 font-medium">Review generated text. Switch tabs for details.</p>
                    </div>
                </div>

                {/* Global Regenerate */}
                {posts.length > 1 && (
                    <button 
                        onClick={handleRegenerateAll} 
                        disabled={loading || isImprovingElement} 
                        className="text-xs font-bold text-cream/60 hover:text-slate-blue flex items-center transition-colors disabled:opacity-50 px-4 py-2 rounded-xl hover:bg-white/5"
                    >
                        <FiRefreshCw className={`mr-2 h-3 w-3 ${regenerationTarget === 'all' ? 'animate-spin' : ''}`}/> 
                        Regenerate All
                    </button>
                )}
            </div>

            <div className="p-8 space-y-6">
                
                {posts.map((post, index) => (
                    <div key={post.id || index} className="p-1 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/10 shadow-lg hover:shadow-xl transition-all">
                        <div className="bg-warm-dark/40 rounded-xl p-5 backdrop-blur-sm">
                            <div className="flex justify-between items-start mb-4 border-b border-white/10 pb-3">
                                {/* Subject */}
                                <div className="flex-1 mr-4">
                                    <span className="text-[10px] font-black text-slate-blue uppercase tracking-widest">Subject</span>
                                    <p className="text-sm font-medium text-white mt-1">{post.subject}</p>
                                </div>
                                
                                <div className="flex space-x-2 shrink-0">
                                    <button onClick={() => handleImproveClick(index)} disabled={loading || isImprovingElement || !hasContent(post)} className="p-2 text-cream/40 hover:text-amber-400 hover:bg-amber-400/10 rounded-lg transition-all" title="Improve with AI">
                                        <FiZap className="h-4 w-4"/>
                                    </button>
                                    <button onClick={() => handleRegeneratePost(index)} disabled={loading || isImprovingElement} className="p-2 text-cream/40 hover:text-slate-blue hover:bg-slate-blue/10 rounded-lg transition-all" title="Regenerate">
                                        <FiRefreshCw className={`h-4 w-4 ${regenerationTarget === index ? 'animate-spin' : ''}`}/>
                                    </button>
                                </div>
                            </div>
                            
                            {(post.content === null || post.content === undefined) ? (
                                <div className="w-full h-32 bg-white/5 rounded-xl flex items-center justify-center border border-dashed border-white/10">
                                    <FiLoader className="animate-spin text-cream/40 h-6 w-6" />
                                    <span className="ml-3 text-xs font-bold text-cream/40 uppercase tracking-widest">Writing content...</span>
                                </div>
                            ) : (
                                <>
                                    <VariantEditor
                                        post={post}
                                        index={index}
                                        onEdit={onEditPostText}
                                        disabled={loading || isImprovingElement}
                                        adjustments={adjustments}
                                    />

                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className="p-8 border-t border-white/10 flex justify-end bg-warm-dark/20 backdrop-blur-sm">
                <button onClick={onConfirmPosts} disabled={!canConfirm} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-sm font-bold rounded-2xl shadow-lg text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-95">
                    <FiCheckCircle className="mr-2 h-5 w-5" />
                    Confirm & Add Visuals
                </button>
            </div>

            {/* Improve Modal */}
            <Modal isOpen={showImproveModal} onClose={() => setShowImproveModal(false)} title="Improve Content">
                 <div className="space-y-4">
                     <p className="text-sm text-cream/60">How should the AI improve this text?</p>
                     <textarea rows="3" value={improveInstruction} onChange={(e) => setImproveInstruction(e.target.value)} className="block w-full bg-white/5 border border-white/10 text-cream text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-slate-blue/50 focus:ring-1 focus:ring-slate-blue/50" placeholder="e.g., Make it funnier, add more hashtags, shorten it..." disabled={isImprovingElement} />
                     <div className="flex justify-end gap-3 pt-2">
                         <button onClick={() => setShowImproveModal(false)} disabled={isImprovingElement} className="px-4 py-2 border border-white/10 rounded-xl text-sm font-bold text-cream bg-white/5 hover:bg-white/10">Cancel</button>
                         <button onClick={handleConfirmImprove} disabled={isImprovingElement || !improveInstruction.trim()} className="inline-flex items-center px-6 py-2 border border-transparent rounded-xl shadow-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50">
                             {isImprovingElement ? <FiLoader className="mr-2 animate-spin"/> : <FiZap className="mr-2"/>} Improve
                         </button>
                     </div>
                 </div>
            </Modal>
        </div>
    );
}

PostPreviewer.propTypes = {
    project: PropTypes.object.isRequired,
    posts: PropTypes.array.isRequired,
    onGeneratePosts: PropTypes.func.isRequired,
    onImprovePostElement: PropTypes.func.isRequired,
    onEditPostText: PropTypes.func.isRequired,
    onConfirmPosts: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    adjustments: PropTypes.object,
};

export default PostPreviewer;
